import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { getSession } from '../lib/session.js'
import SafeHtml from '../components/ui/SafeHtml.jsx'
import ArcGauge from '../components/ui/ArcGauge.jsx'
import RecapSection from '../components/ui/RecapSection.jsx'

function formatDuration(start, end) {
  if (!start) return '—'
  const ms = Math.max(0, (end ? new Date(end) : new Date()) - new Date(start))
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

export default function SoloPlayerStatsPage() {
  const { code }   = useParams()
  const navigate   = useNavigate()
  const session    = getSession(code)
  const [game, setGame]       = useState(null)
  const [stats, setStats]     = useState(null)
  const [recap, setRecap]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedSt, setSelectedSt] = useState(null)
  const [emailModal, setEmailModal]   = useState(false)
  const [emailInput, setEmailInput]   = useState('')
  const [emailStatus, setEmailStatus] = useState('idle') // idle | sending | sent | error

  useEffect(() => {
    const headers = session?.token ? { 'x-session-token': session.token } : {}
    Promise.all([
      apiFetch(`/api/games/${code}`),
      apiFetch(`/api/games/${code}/stats`),
    ]).then(([g, s]) => {
      setGame(g)
      setStats(s)
    }).catch(() => {}).finally(() => setLoading(false))

    if (session?.token) {
      apiFetch(`/api/karts/${code}/recap`, { headers })
        .then(setRecap)
        .catch(() => {})
    }
  }, [code])

  async function handleSendEmail(e) {
    e.preventDefault()
    setEmailStatus('sending')
    try {
      const headers = session?.token ? { 'x-session-token': session.token } : {}
      await apiFetch(`/api/karts/${code}/send-recap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ email: emailInput }),
      })
      setEmailStatus('sent')
    } catch {
      setEmailStatus('error')
    }
  }

  if (loading) {
    return (
      <div className="h-[100dvh] bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-white/70 text-sm">Chargement…</p>
        </div>
      </div>
    )
  }

  const g = stats?.global ?? { total: 0, correctes: 0, timeouts: 0, incorrectes: 0, taux: 0 }
  const duration = formatDuration(game?.cree_le, game?.termine_le)
  const taux = g.taux ?? (g.total > 0 ? Math.round(g.correctes / g.total * 100) : 0)

  return (
    <div className="min-h-[100dvh] bg-gray-900 flex flex-col select-none">

      {/* Header */}
      <header className="bg-gray-900/95 px-4 py-3 flex items-center gap-3 border-b border-white/10 shrink-0">
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="text-white/50 hover:text-white transition p-1 -ml-1 shrink-0 text-3xl leading-none"
          aria-label="Retour"
        >
          ‹
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-white font-semibold text-base truncate">
            {game?.decks?.titre ?? '…'}
          </h1>
          <p className="text-white/40 text-xs">Partie terminée · {duration}</p>
        </div>
        <div className="text-3xl shrink-0">🏁</div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-4 max-w-lg mx-auto w-full">

        {!stats || g.total === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <span className="text-4xl">📊</span>
            <p className="text-white/40 text-sm">Aucune réponse enregistrée.</p>
          </div>
        ) : selectedSt ? (
          /* ── Vue détail sous-thème ── */
          <div className="space-y-3">
            <button
              onClick={() => setSelectedSt(null)}
              className="flex items-center gap-1 text-white/50 hover:text-white transition text-sm"
            >
              ‹ Retour aux sous-thèmes
            </button>
            <div className="bg-gray-800 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
              <ArcGauge
                correctes={selectedSt.correctes} timeouts={selectedSt.timeouts ?? 0}
                incorrectes={selectedSt.incorrectes} total={selectedSt.total} size={90}
              />
              <div>
                <p className="text-white font-semibold">{selectedSt.nom}</p>
                <p className="text-white/50 text-sm mt-0.5">
                  {selectedSt.correctes} ✅&nbsp; {selectedSt.timeouts ?? 0} ⌛&nbsp; {selectedSt.incorrectes} ❌
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {(selectedSt.questions ?? []).map(q => {
                const qtaux = q.total > 0 ? Math.round(q.correctes / q.total * 100) : 0
                return (
                  <div key={q.id} className="bg-gray-800 border border-white/10 rounded-xl p-3 flex items-center gap-3">
                    <ArcGauge
                      correctes={q.correctes} timeouts={q.timeouts ?? 0}
                      incorrectes={q.incorrectes} total={q.total} size={60}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm line-clamp-2 leading-snug"><SafeHtml html={q.enonce} /></p>
                      <p className={`text-xs mt-1 font-medium ${qtaux >= 75 ? 'text-green-400' : qtaux >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {qtaux}% — {q.correctes} ✅&nbsp;{q.timeouts ?? 0} ⌛&nbsp;{q.incorrectes} ❌
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          /* ── Vue principale ── */
          <>
            {/* Score global */}
            <div className="bg-gray-800 border border-white/10 rounded-2xl p-5 flex items-center gap-5">
              <ArcGauge {...g} size={140} />
              <div className="flex-1">
                <p className="text-white font-bold text-3xl">{taux}%</p>
                <p className="text-white/60 text-sm mt-1">
                  {g.correctes} bonne{g.correctes !== 1 ? 's' : ''} sur {g.total}
                </p>
                {(g.timeouts ?? 0) > 0 && (
                  <p className="text-white/40 text-xs mt-0.5">
                    {g.timeouts} délai{g.timeouts !== 1 ? 's' : ''} dépassé{g.timeouts !== 1 ? 's' : ''}
                  </p>
                )}
                <p className={`text-sm font-semibold mt-3 ${taux >= 75 ? 'text-green-400' : taux >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {taux >= 75 ? '🎉 Maîtrisé !' : taux >= 50 ? '👍 Acquis' : '📚 À réviser'}
                </p>
              </div>
            </div>

            {/* Légende */}
            <div className="flex items-center gap-4 px-1 text-xs text-white/40">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />Bonnes réponses</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-200 inline-block" />Délai dépassé</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-600 border border-white/20 inline-block" />Mauvaises</span>
            </div>

            {/* Sous-thèmes */}
            {(stats.sous_themes ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-white/40 text-xs uppercase tracking-widest px-1">Par sous-thème</p>
                {(stats.sous_themes ?? []).map(st => {
                  const stTaux = st.total > 0 ? Math.round(st.correctes / st.total * 100) : 0
                  return (
                    <button
                      key={st.id}
                      onClick={() => setSelectedSt(st)}
                      className="w-full bg-gray-800 border border-white/10 rounded-xl p-3 flex items-center gap-3 hover:border-white/30 transition text-left"
                    >
                      <ArcGauge
                        correctes={st.correctes} timeouts={st.timeouts ?? 0}
                        incorrectes={st.incorrectes} total={st.total} size={60}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{st.nom}</p>
                        <p className={`text-xs mt-0.5 ${stTaux >= 75 ? 'text-green-400' : stTaux >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                          {stTaux}% — {st.correctes} ✅&nbsp;{st.timeouts ?? 0} ⌛&nbsp;{st.incorrectes} ❌
                        </p>
                      </div>
                      <span className="text-white/30 text-xl leading-none">›</span>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Analyse personnalisée Maîtrisé / Acquis / À réviser */}
            {recap && (recap.sous_themes ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-white/40 text-xs uppercase tracking-widest px-1">Analyse personnalisée</p>
                {recap.sous_themes.map((st, i) => (
                  <div key={i} className="bg-white/5 rounded-2xl p-4">
                    <h3 className="text-white font-semibold text-sm mb-3">{st.nom}</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {st.maitrise.length  > 0 && <RecapSection label="Maîtrisé"  color="green" items={st.maitrise} />}
                      {st.acquis.length    > 0 && <RecapSection label="Acquis"    color="amber" items={st.acquis} />}
                      {st.a_reviser.length > 0 && <RecapSection label="À réviser" color="red"   items={st.a_reviser} />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Bouton e-mail — visible si le joueur a un token (a bien joué dans cette partie) */}
        {session?.token && !selectedSt && (
          <div>
            <button
              onClick={() => { setEmailModal(true); setEmailStatus('idle') }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition font-medium flex items-center justify-center gap-2"
            >
              <span>✉️</span> Recevoir par mail
            </button>
          </div>
        )}

        {/* Bouton retour */}
        <div className="pb-4">
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full py-3 text-white/70 border border-white/20 rounded-xl hover:bg-white/10 transition font-medium"
          >
            Retour à l'accueil
          </button>
        </div>
      </main>

      {/* Modal envoi e-mail */}
      {emailModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setEmailModal(false) }}
        >
          <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-sm p-5 space-y-4">
            {emailStatus === 'sent' ? (
              <div className="text-center space-y-3 py-2">
                <p className="text-4xl">✅</p>
                <p className="text-white font-semibold">Mail envoyé !</p>
                <p className="text-white/50 text-sm">Vérifie ta boîte de réception.</p>
                <button
                  onClick={() => setEmailModal(false)}
                  className="w-full py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition font-medium text-sm"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div>
                  <h2 className="text-white font-semibold text-base">Recevoir par mail</h2>
                  <p className="text-white/50 text-sm mt-1">
                    Indique l'adresse à laquelle tu souhaites recevoir cette page de résultats.
                  </p>
                </div>
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  placeholder="ton@email.fr"
                  className="w-full bg-gray-900 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-indigo-500 transition"
                />
                {emailStatus === 'error' && (
                  <p className="text-red-400 text-xs">Une erreur est survenue. Réessaie.</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEmailModal(false)}
                    className="flex-1 py-2.5 text-white/60 border border-white/20 rounded-xl hover:bg-white/10 transition font-medium text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={emailStatus === 'sending'}
                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl transition font-medium text-sm"
                  >
                    {emailStatus === 'sending' ? 'Envoi…' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
